import { useEffect, useRef, useState } from 'react'

const ESG_PROFILE_URL =
  'https://esg.churchgatepartners.com/login/companyprofile?id=3100390031003700240024004100530048004F004B0041004E0041004E00590041004100560041004E004900410053004800570049004E00490024002400'
export default function EsgProfileEmbed() {
  const [isLoaded, setIsLoaded] = useState(false)
  const [isBlocked, setIsBlocked] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const frameWrapperRef = useRef<HTMLDivElement>(null)

  /* If the profile hasn't rendered in 15s it is almost always blocked by the
     provider's X-Frame-Options / CSP, so we surface the direct link instead. */
  useEffect(() => {
    if (isLoaded) return
    const timeoutId = setTimeout(() => setIsBlocked(true), 15000)
    return () => clearTimeout(timeoutId)
  }, [isLoaded])

  useEffect(() => {
    const syncFullscreen = () =>
      setIsFullscreen(Boolean(document.fullscreenElement))
    document.addEventListener('fullscreenchange', syncFullscreen)
    return () => document.removeEventListener('fullscreenchange', syncFullscreen)
  }, [])

  const toggleFullscreen = () => {
    const node = frameWrapperRef.current
    if (!node) return
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => setIsFullscreen(false))
    } else {
      node.requestFullscreen().catch(() => setIsFullscreen(false))
    }
  }

  return (
    <section className="pt-6 md:pt-8">
      <div className="flex flex-col gap-3 pb-4 sm:flex-row sm:items-end sm:justify-between">
        <p className="max-w-xl text-sm leading-relaxed text-green/80 md:text-base">
        </p>

        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={toggleFullscreen}
            className="hidden items-center gap-2 rounded-md border border-green px-3 py-2 text-sm font-semibold text-green transition-colors hover:bg-green hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green sm:inline-flex"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              {isFullscreen ? (
                <path d="M9 3v6H3M15 3v6h6M9 21v-6H3M15 21v-6h6" />
              ) : (
                <path d="M3 9V3h6M21 9V3h-6M3 15v6h6M21 15v6h-6" />
              )}
            </svg>
            {isFullscreen ? 'Exit full screen' : 'Full screen'}
          </button>

          <a
            href={ESG_PROFILE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-md bg-green px-3 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green"
          >
            Open in a new tab
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M14 3h7v7M21 3l-9 9M19 14v6H4V5h6" />
            </svg>
          </a>
        </div>
      </div>

      <div
        ref={frameWrapperRef}
        className={`relative overflow-hidden border border-green/30 bg-white shadow-[0_10px_30px_-20px_rgba(25,60,52,0.6)] ${
          isFullscreen
            ? 'h-screen w-screen rounded-none'
            : 'h-[68vh] min-h-[460px] rounded-xl md:h-[76vh] xl:h-[80vh]'
        }`}
      >
        <iframe
          src={ESG_PROFILE_URL}
          title="BharatRohan Airborne Innovations ESG Profile"
          onLoad={() => {
            setIsLoaded(true)
            setIsBlocked(false)
          }}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
          className="h-full w-full border-0"
        />

        {!isLoaded && (
          <div className="absolute inset-0 grid place-content-center gap-3 bg-white px-6 text-center">
            {isBlocked ? (
              <>
                <p className="font-semibold text-green">
                  The ESG profile can’t be displayed here
                </p>
                <p className="max-w-sm text-sm text-green/70">
                  The provider may be blocking embedded views, or your browser is
                  blocking third-party cookies. Open the profile in a new tab to
                  see the full report.
                </p>
                <a
                  href={ESG_PROFILE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mx-auto rounded-md bg-green px-4 py-2 text-sm font-semibold text-white"
                >
                  Open the ESG profile
                </a>
              </>
            ) : (
              <>
                <span
                  className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-green/25 border-t-green motion-reduce:animate-none"
                  aria-hidden="true"
                />
                <p className="text-sm font-semibold text-green">
                  Loading the ESG profile…
                </p>
              </>
            )}
          </div>
        )}
      </div>

      <p className="pt-3 text-xs text-green/60 sm:hidden">
        The profile is a wide report — scroll sideways inside the panel, or open
        it in a new tab for a better view on a phone.
      </p>
    </section>
  )
}
